import { useContext, useEffect, useRef, useState } from 'react';
import CHAT_EVENTS from '../constants/ChatEvents';
import { ChatContext } from '../context/ChatContext';
import { SYSTEM_ACTIONS } from '../constants/ChatSystemActions';
import { useQuery } from '@apollo/client';
import conversations from 'src/gql/queries/conversations';
import { useSelector } from 'react-redux';
import store from 'src/redux/store';

const useChatUnreadMessages = (isChatActive) => {
  const { socket } = useContext(ChatContext);
  const [showUnreadMessages, setShowUnreadMessages] = useState(false);
  const user = useSelector((state) => state.user);
  const chatConversations = useSelector(
    (state) => state.chat.conversations.entities
  );
  const chatConversationsRef = useRef(chatConversations);
  useEffect(() => {
    chatConversationsRef.current = chatConversations;
  }, [chatConversations]);

  useQuery(conversations.CHECK_UNREAD_MESSAGES_BY_USER_ID, {
    variables: {
      userId: user.id,
      workspaceId: user.currentWorkspace,
    },
    onCompleted: (data) => {
      const hasUnreadMessages = data.getConversationsByWorkspaceId.some(
        (conversation) => !conversation.isView && !conversation.situationId
      );
      setShowUnreadMessages(hasUnreadMessages);
    },
  });

  useEffect(() => {
    if (!socket || isChatActive) return;

    const handleUnreadMessages = (payload) => {
      if (
        payload.message.type === 'system' &&
        payload.message?.metaData.action ===
          SYSTEM_ACTIONS.FINISHED_CONVERSATION
      )
        return;

      // Skip situation chats — check both the conversation entity and the
      // situations store because the conversation may not be in Redux yet
      // (race condition on page load).
      const conv = chatConversationsRef.current[payload.conversationId];
      if (conv?.situationId) return;

      const situations = store.getState().situations.entities;
      const isSituationChat = Object.values(situations).some(
        (s) => s.conversationId === payload.conversationId
      );
      if (isSituationChat) return;

      if (!isChatActive && !showUnreadMessages) {
        setShowUnreadMessages(true);
      }
    };

    socket.on(CHAT_EVENTS.SEND_MESSAGE, handleUnreadMessages);
    socket.on(CHAT_EVENTS.JOIN_CHAT, handleUnreadMessages);
    socket.on(CHAT_EVENTS.JOIN_GROUP, handleUnreadMessages);

    return () => {
      socket.off(CHAT_EVENTS.SEND_MESSAGE, handleUnreadMessages);
      socket.off(CHAT_EVENTS.JOIN_CHAT, handleUnreadMessages);
      socket.off(CHAT_EVENTS.JOIN_GROUP, handleUnreadMessages);
    };
  }, [socket, isChatActive]);

  const clearUnreadMessages = () => setShowUnreadMessages(false);

  return {
    hasUnreadMessages: showUnreadMessages,
    clearUnreadMessages,
  };
};

export default useChatUnreadMessages;
